/**
 * FoodPulseArticles — tile directory of every article propagated from
 * foodpulse.net. Filters by category chip and free-text search.
 * Categories are now data-driven from the API counts.
 *
 * Route: /food-pulse/articles                  → all
 *        /food-pulse/articles?category=health  → single category
 * Backend: GET /api/food-articles, /api/food-articles/categories
 */
import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import {
  Search, Sparkles, Newspaper, Leaf, Apple, Trash2, HeartPulse,
  Wheat, ChevronRight, ShoppingBag, HandHeart, Users, Utensils, MapPin,
} from "lucide-react";
import SeoHead from "@/components/SeoHead";

const API = process.env.REACT_APP_BACKEND_URL;

// All active categories with display metadata
const CATEGORY_META = {
  discover:     { label: "Discover",    icon: Leaf,       kicker: "food discovery", color: "#166534" },
  health:       { label: "Health",      icon: HeartPulse, kicker: "wellbeing",      color: "#9D174D" },
  farming:      { label: "Farming",     icon: Wheat,      kicker: "from the farm",  color: "#92400E" },
  "food-waste": { label: "Food Waste",  icon: Trash2,     kicker: "less waste",     color: "#065F46" },
  recipes:      { label: "Recipes",     icon: Utensils,   kicker: "in the kitchen", color: "#9A3412" },
  community:    { label: "Community",   icon: Users,      kicker: "together",       color: "#1E40AF" },
  news:         { label: "News",        icon: Newspaper,  kicker: "latest",         color: "#374151" },
  marketplace:  { label: "Marketplace", icon: ShoppingBag,kicker: "buy & sell",     color: "#6B21A8" },
  deals:        { label: "Deals",       icon: HandHeart,  kicker: "local deals",    color: "#0E7490" },
  food:         { label: "Food Guide",  icon: Apple,      kicker: "know your food", color: "#854D0E" },
  places:       { label: "Places",      icon: MapPin,     kicker: "food places",    color: "#0F766E" },
};

const HERO_HEADLINES = {
  "food-waste": <><span>Save the harvest.</span> <em style={{ color: "#B25D38", fontStyle: "italic" }}>Share the goodness.</em></>,
  health:       <><span>Eat well,</span> <em style={{ color: "#B25D38", fontStyle: "italic" }}>live well.</em></>,
  farming:      <><span>From the field</span> <em style={{ color: "#B25D38", fontStyle: "italic" }}>to your table.</em></>,
  recipes:      <><span>Real food</span> <em style={{ color: "#B25D38", fontStyle: "italic" }}>for real life.</em></>,
  community:    <><span>Together we</span> <em style={{ color: "#B25D38", fontStyle: "italic" }}>feed better.</em></>,
  discover:     <><span>Discover food,</span> <em style={{ color: "#B25D38", fontStyle: "italic" }}>know what matters.</em></>,
  news:         <><span>The community,</span> <em style={{ color: "#B25D38", fontStyle: "italic" }}>told in food.</em></>,
  marketplace:  <><span>Find, buy, sell</span> <em style={{ color: "#B25D38", fontStyle: "italic" }}>food locally.</em></>,
  deals:        <><span>Fresh deals,</span> <em style={{ color: "#B25D38", fontStyle: "italic" }}>every day.</em></>,
  food:         <><span>Know your food,</span> <em style={{ color: "#B25D38", fontStyle: "italic" }}>eat better.</em></>,
  places:       <><span>Great food,</span> <em style={{ color: "#B25D38", fontStyle: "italic" }}>close to home.</em></>,
};

function prettyDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  } catch { return ""; }
}

export default function FoodPulseArticles() {
  const [params, setParams] = useSearchParams();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  // Dynamic ordered list from the API
  const [categoryOrder, setCategoryOrder] = useState([]);

  const q = params.get("q") || "";
  const category = params.get("category") || "";

  const PAGE_SIZE = 24;

  useEffect(() => {
    const meta = CATEGORY_META[category];
    document.title = meta
      ? `${meta.label} — Food Pulse`
      : "Food Pulse — Articles";
  }, [category]);

  // Load per-category counts once — drives the chip strip dynamically
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/api/food-articles/categories`);
        if (cancelled) return;
        const map = {};
        const ordered = [];
        (res.data.by_category || []).forEach((c) => {
          if (c.category && c.count > 0) {
            map[c.category] = c.count;
            ordered.push(c.category);
          }
        });
        setCounts({ ...map, total: res.data.total });
        // Show only categories that have articles + are in CATEGORY_META
        setCategoryOrder(ordered.filter(c => CATEGORY_META[c]));
      } catch { /* optional */ }
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setItems([]); // reset on filter change
    (async () => {
      try {
        const res = await axios.get(`${API}/api/food-articles`, {
          params: { q: q || undefined, category: category || undefined, limit: PAGE_SIZE, offset: 0 },
        });
        if (cancelled) return;
        setItems(res.data.items || []);
        setTotal(res.data.total || 0);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [q, category]);

  async function loadMore() {
    setLoadingMore(true);
    try {
      const res = await axios.get(`${API}/api/food-articles`, {
        params: { q: q || undefined, category: category || undefined, limit: PAGE_SIZE, offset: items.length },
      });
      setItems(prev => [...prev, ...(res.data.items || [])]);
      setTotal(res.data.total || 0);
    } finally {
      setLoadingMore(false);
    }
  }

  const setParam = (key, val) => {
    const next = new URLSearchParams(params);
    if (val) next.set(key, val); else next.delete(key);
    setParams(next, { replace: true });
  };

  const activeMeta = CATEGORY_META[category];
  const heroHeadline = HERO_HEADLINES[category] || (
    <><span>Share a recipe.</span> <em style={{ color: "#B25D38", fontStyle: "italic" }}>Feed a neighbour.</em></>
  );

  return (
    <div className="min-h-screen" style={{ background: "#FBF7F0" }} data-testid="foodpulse-articles">
      <SeoHead
        section="food-pulse"
        title={activeMeta ? `${activeMeta.label} — Food Pulse` : "Food Pulse — Articles"}
        description="Articles, recipes, guides and community stories from the Food Pulse network. Fight food waste, celebrate seasonal produce, and share what you cook."
        canonical={`https://foodpulse.net/food-pulse/articles${category ? `?category=${category}` : ""}`}
      />

      {/* Breadcrumb */}
      <nav aria-label="Breadcrumb"
           className="max-w-6xl mx-auto px-4 pt-4 flex items-center gap-1 text-xs opacity-60"
           data-testid="articles-breadcrumb">
        <Link to="/food-pulse" className="hover:underline" style={{ color: "#B25D38" }}>FoodPulse</Link>
        <ChevronRight className="w-3 h-3 opacity-50" />
        {activeMeta ? (
          <>
            <Link to="/food-pulse/articles" className="hover:underline" style={{ color: "#B25D38" }}>Articles</Link>
            <ChevronRight className="w-3 h-3 opacity-50" />
            <span>{activeMeta.label}</span>
          </>
        ) : (
          <span>Articles</span>
        )}
      </nav>

      {/* Hero — classic serif treatment */}
      <section style={{ background: "#F0E2C7" }} className="border-b mt-4" data-testid="articles-hero">
        <div className="max-w-6xl mx-auto px-4 py-12">
          <div className="text-[10px] sm:text-xs font-mono uppercase tracking-widest mb-4" style={{ color: "#8B5E10" }}>
            — Food Pulse · {activeMeta?.kicker || "articles from the desk"}
          </div>
          <h1
            className="font-display leading-[1.05] tracking-tight text-[38px] sm:text-[56px] lg:text-[72px] mb-4"
            style={{ color: "#1A1613" }}
          >
            {heroHeadline}
          </h1>
          <p className="text-sm sm:text-base max-w-2xl" style={{ color: "#4B3A20" }}>
            {activeMeta
              ? `Explore ${activeMeta.label.toLowerCase()} articles — trusted, community-sourced stories from the Food Pulse network.`
              : "Discover local food deals, fight food waste, and explore recipes using surplus ingredients. Join our community of farmers, kitchens, and everyday cooks."}
          </p>
        </div>
      </section>

      {/* Category chips + search */}
      <div className="max-w-6xl mx-auto px-4 pt-8">
        <div className="flex flex-wrap gap-2 items-center" data-testid="articles-chips">
          <button
            type="button"
            onClick={() => setParam("category", "")}
            data-testid="articles-cat-all"
            className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${category === "" ? "text-white" : "hover:bg-white"}`}
            style={category === ""
              ? { background: "#1A1613", borderColor: "#1A1613" }
              : { background: "#fff", borderColor: "#C79A3F", color: "#3E2A0A" }}
          >
            All {counts.total ? `· ${counts.total}` : ""}
          </button>
          {categoryOrder.map((c) => {
            const meta = CATEGORY_META[c];
            if (!meta) return null;
            const Icon = meta.icon;
            const active = category === c;
            const n = counts[c];
            return (
              <button
                key={c}
                type="button"
                onClick={() => setParam("category", c)}
                data-testid={`articles-cat-${c}`}
                className={`inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full border transition-colors ${active ? "text-white" : "hover:bg-white"}`}
                style={active
                  ? { background: "#B25D38", borderColor: "#B25D38" }
                  : { background: "#fff", borderColor: "#C79A3F", color: "#3E2A0A" }}
              >
                <Icon className="w-3.5 h-3.5" />
                {meta.label} {n ? `· ${n}` : ""}
              </button>
            );
          })}
          <div className="ml-auto flex items-center gap-2 rounded-full border bg-white px-3 py-1.5 min-w-[180px]"
               style={{ borderColor: "#C79A3F" }}>
            <Search className="w-4 h-4 opacity-60" />
            <input
              type="search"
              data-testid="articles-search"
              value={q}
              onChange={(e) => setParam("q", e.target.value)}
              placeholder="Search articles…"
              className="w-full bg-transparent outline-none text-sm"
              aria-label="Search articles"
            />
          </div>
        </div>
      </div>

      {/* Grid */}
      <section className="max-w-6xl mx-auto px-4 py-8" aria-label="Article results">
        {loading ? (
          <div className="text-sm opacity-70" data-testid="articles-loading">Loading articles…</div>
        ) : items.length === 0 ? (
          <div className="rounded-2xl border p-10 text-center bg-white"
               style={{ borderColor: "#C79A3F" }} data-testid="articles-empty">
            <Sparkles className="w-6 h-6 mx-auto mb-2" style={{ color: "#8B5E10" }} />
            <div className="font-display text-lg mb-1">No articles match this filter yet.</div>
            <div className="text-sm opacity-70">Try a different category or clear your search.</div>
          </div>
        ) : (
          <>
            <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3" data-testid="articles-grid">
              {items.map((a) => {
                const catMeta = CATEGORY_META[a.category];
                return (
                  <Link
                    key={a.slug}
                    to={`/food-pulse/articles/${a.slug}`}
                    className="group bg-white rounded-2xl overflow-hidden border hover:shadow-md transition-shadow"
                    style={{ borderColor: "#E9D8B4" }}
                    data-testid={`article-card-${a.slug}`}
                  >
                    {a.cover_image ? (
                      <div className="aspect-[16/10] overflow-hidden bg-black/5">
                        <img
                          src={a.cover_image}
                          alt={a.title}
                          loading="lazy"
                          className="w-full h-full object-cover group-hover:scale-[1.02] transition-transform"
                        />
                      </div>
                    ) : (
                      <div className="aspect-[16/10] flex items-center justify-center"
                           style={{ background: "linear-gradient(135deg,#F0E2C7,#FBF7F0)" }}>
                        <Newspaper className="w-8 h-8 opacity-30" style={{ color: "#8B5E10" }} />
                      </div>
                    )}
                    <div className="p-5">
                      {catMeta && (
                        <div className="text-[10px] font-mono uppercase tracking-widest mb-2"
                             style={{ color: catMeta.color }}>
                          {a.tag || catMeta.label}
                        </div>
                      )}
                      <h3 className="font-display text-xl leading-tight" style={{ color: "#1A1613" }}>
                        {a.title}
                      </h3>
                      {a.excerpt && (
                        <p className="text-sm mt-2 opacity-80 line-clamp-3">{a.excerpt}</p>
                      )}
                      <div className="mt-3 flex flex-wrap items-center gap-2 text-xs opacity-60">
                        {a.published_at && <span>{prettyDate(a.published_at)}</span>}
                        {a.retax_at && (
                          <span className="rounded-full px-2 py-0.5 bg-amber-50 text-amber-700 font-medium">
                            Reviewed
                          </span>
                        )}
                      </div>
                    </div>
                  </Link>
                );
              })}
            </div>
            {/* Load more */}
            {items.length < total && (
              <div className="mt-10 text-center" data-testid="articles-load-more-wrap">
                <button
                  type="button"
                  onClick={loadMore}
                  disabled={loadingMore}
                  data-testid="articles-load-more-btn"
                  className="inline-flex items-center gap-2 rounded-full px-8 py-3 text-sm font-semibold border transition-all hover:shadow-md disabled:opacity-60"
                  style={{ borderColor: "#C79A3F", color: "#3E2A0A", background: "#fff" }}
                >
                  {loadingMore ? "Loading…" : `Load more · ${total - items.length} remaining`}
                </button>
                <div className="mt-2 text-xs opacity-50" data-testid="articles-count-label">
                  Showing {items.length} of {total} articles
                </div>
              </div>
            )}
          </>
        )}
      </section>
    </div>
  );
}
